// External dependencies
import { useEffect, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import type { FC, KeyboardEvent } from 'react';

// Internal components
import ReasonForChange from '@/components/Shared/Latest/ReasonForChange';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';

interface BulkReasonForChangeProps {
  onClick: (reasonForChange?: string) => Promise<void>;
  onCancel?: () => void;
  closeModal: () => void;
  title: string;
}

interface BulkReasonForChangeFormData {
  reason_for_change?: string;
}

/**
 * Dedicated "Reason for change" modal. Auto-focuses the reason textarea on open
 * and submits the form (routed through react-hook-form validation) when the user
 * presses Enter; Shift+Enter still inserts a newline. The reason is trimmed
 * before submitting so an Enter submit matches the shared field's onBlur trim.
 */
export const BulkReasonForChange: FC<BulkReasonForChangeProps> = ({ onClick, onCancel, title, closeModal }) => {
  const formMethods = useForm<BulkReasonForChangeFormData>();
  const { handleSubmit } = formMethods;
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    formRef.current?.querySelector<HTMLTextAreaElement>('#reason_for_change')?.focus();
  }, []);

  const onSubmit = (form: BulkReasonForChangeFormData): void => {
    onClick(form.reason_for_change?.trim());
    closeModal();
  };

  const handleCancel = (): void => {
    onCancel?.();
    closeModal();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>): void => {
    if (event.key === 'Enter' && !event.shiftKey && (event.target as HTMLElement).id === 'reason_for_change') {
      event.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  return (
    <ModalContainer size="narrow" className="bg-white">
      <ModalHeader title={`${title || 'Data has changed'}`} className="pa3 bb b--moon-gray w-100" />
      <FormProvider {...formMethods}>
        <form
          ref={formRef}
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={handleKeyDown}
          data-testid="bulk-reason-for-change-modal"
        >
          <div className="pa3">
            <ReasonForChange />
          </div>
          <ModalActions className="pa3 mt3 bt b--moon-gray" onSubmit={handleSubmit(onSubmit)} onCancel={handleCancel} />
        </form>
      </FormProvider>
    </ModalContainer>
  );
};
